import type { Profile } from '@cadencia/contracts';
import { useLiveQuery } from 'dexie-react-hooks';
import { getSyncState } from './db.js';
import { getProfile } from './repositories/profileRepo.js';

/**
 * ¿No hay perfil, o es que todavía no lo hemos mirado?
 *
 * Es la regla de `bloqueActivo.ts` y de `preferenciaIA.ts`, aplicada al perfil,
 * y por el mismo motivo: en un dispositivo recién estrenado Dexie está vacía
 * mientras viaja la primera bajada. Si Inicio mandara al test inicial en esa
 * ventana, el atleta volvería a contestarlo todo y crearía un SEGUNDO perfil y
 * un SEGUNDO bloque encima de los suyos, que es exactamente lo que pasó con los
 * bloques el 22/09/2026 (CLAUDE.md, «“No hay fila” y “no la he mirado”»).
 *
 * Se sabe que no hay perfil si **ha terminado alguna bajada** y aun así no hay.
 */
export interface EstadoPerfil {
  perfil: Profile | null;
  /** `false` mientras «no hay perfil» pueda significar «no lo he mirado». */
  conocido: boolean;
}

export async function leerPerfil(userId: string | null): Promise<EstadoPerfil> {
  if (userId === null) return { perfil: null, conocido: false };

  const perfil = (await getProfile(userId)) ?? null;
  if (perfil !== null) return { perfil, conocido: true };

  const estado = await getSyncState();
  return { perfil: null, conocido: (estado?.lastPullAt ?? null) !== null };
}

/** `undefined` mientras la consulta no ha vuelto. */
export function usePerfil(userId: string | null): EstadoPerfil | undefined {
  return useLiveQuery(async () => leerPerfil(userId), [userId]);
}

/**
 * Qué le falta al atleta respecto al test inicial.
 *
 * - `sin_perfil`: no hay perfil. Es el estado tras borrar el historial, y el de
 *   una cuenta nueva: la aplicación empieza por el test.
 * - `sin_test`: hay perfil, pero de antes del test —sin disponibilidad—. Se
 *   invita a hacerlo; no se obliga, porque ese atleta ya tiene su plan.
 * - `completo`: nada que hacer.
 * - `desconocido`: todavía no se sabe. No se ofrece nada.
 */
export type SituacionDelTest = 'sin_perfil' | 'sin_test' | 'completo' | 'desconocido';

export function situacionDelTest(estado: EstadoPerfil | undefined): SituacionDelTest {
  if (estado === undefined || !estado.conocido) return 'desconocido';
  if (estado.perfil === null) return 'sin_perfil';
  if (estado.perfil.availability === null) return 'sin_test';
  return 'completo';
}
